// src/views/Usuarios/FiltroUsuario.jsx
import React, { useEffect, useRef, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";

const PAGE_SIZE = 500;

function useDebounced(v, delay = 300) {
  const [val, setVal] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setVal(v), delay);
    return () => clearTimeout(t);
  }, [v, delay]);
  return val;
}

const isAbort = (e) => e?.name === "AbortError" || /aborted|abort/i.test(e?.message || "");

export default function FiltroUsuario({
  initialFilters = { codigo: "", nombre: "", estado: "", clienteId: "", clienteNombre: "", eessId: "", eessNombre: "" },
  onSubmit = () => {},
  onChangeFilters = () => {},
  onClose = () => {},
}) {
  // ====== Campos base ======
  const [codigo, setCodigo] = useState(initialFilters.codigo || "");
  const [nombre, setNombre] = useState(initialFilters.nombre || "");
  const [estado, setEstado] = useState(initialFilters.estado || "");

  // Autocomplete Cliente
  const [cliInput, setCliInput] = useState(initialFilters.clienteNombre || "");
  const debCli = useDebounced(cliInput, 300);
  const [cliItems, setCliItems] = useState([]);
  const [cliOpen, setCliOpen] = useState(false);
  const [cliLoading, setCliLoading] = useState(false);
  const [cliHasMore, setCliHasMore] = useState(false);
  const [cliPage, setCliPage] = useState(0);
  const [clienteSel, setClienteSel] = useState(
    initialFilters.clienteId ? { id: initialFilters.clienteId, nombre: initialFilters.clienteNombre || "" } : null
  );
  const cliBoxRef = useRef(null);
  const cliAbort = useRef(null);

  // Autocomplete EE.SS
  const [eessInput, setEessInput] = useState(initialFilters.eessNombre || "");
  const debEess = useDebounced(eessInput, 300);
  const [eessItems, setEessItems] = useState([]);
  const [eessOpen, setEessOpen] = useState(false);
  const [eessLoading, setEessLoading] = useState(false);
  const [eessHasMore, setEessHasMore] = useState(false);
  const [eessPage, setEessPage] = useState(0);
  const [eessSel, setEessSel] = useState(
    initialFilters.eessId ? { id: initialFilters.eessId, nombre: initialFilters.eessNombre || "" } : null
  );
  const eessBoxRef = useRef(null);
  const eessAbort = useRef(null);

  // Autocomplete Rol
  const [rolInput, setRolInput] = useState(initialFilters.rolNombre || "");
  const debRol = useDebounced(rolInput, 300);
  const [rolItems, setRolItems] = useState([]);
  const [rolOpen, setRolOpen] = useState(false);
  const [rolLoading, setRolLoading] = useState(false);
  const [rolHasMore, setRolHasMore] = useState(false);
  const [rolPage, setRolPage] = useState(0);
  const [rolSel, setRolSel] = useState(
      initialFilters.rolId ? { id: initialFilters.rolId, nombre: initialFilters.rolNombre || "" } : null
  );
  const rolBoxRef = useRef(null);
  const rolAbort = useRef(null);

  // Autocomplete Código (Usuario)
  const [codInput, setCodInput] = useState(initialFilters.codigo || "");
  const debCod = useDebounced(codInput, 300);
  const [codItems, setCodItems] = useState([]);
  const [codOpen, setCodOpen] = useState(false);
  const [codLoading, setCodLoading] = useState(false);
  const [codHasMore, setCodHasMore] = useState(false);
  const [codPage, setCodPage] = useState(0);
  const codBoxRef = useRef(null);
  const codAbort = useRef(null);

  // Autocomplete Nombre (Usuario)
  const [nomInput, setNomInput] = useState(initialFilters.nombre || "");
  const debNom = useDebounced(nomInput, 300);
  const [nomItems, setNomItems] = useState([]);
  const [nomOpen, setNomOpen] = useState(false);
  const [nomLoading, setNomLoading] = useState(false);
  const [nomHasMore, setNomHasMore] = useState(false);
  const [nomPage, setNomPage] = useState(0);
  const nomBoxRef = useRef(null);
  const nomAbort = useRef(null);

  // ===== Rehidratación ante cambios de initialFilters =====
  useEffect(() => {
    setCodigo(initialFilters.codigo || "");
    setNombre(initialFilters.nombre || "");
    setEstado(initialFilters.estado || "");

    setCodInput(initialFilters.codigo || "");
    setNomInput(initialFilters.nombre || "");

    setClienteSel(initialFilters.clienteId ? { id: initialFilters.clienteId, nombre: initialFilters.clienteNombre || "" } : null);
    setCliInput(initialFilters.clienteNombre || "");

    setEessSel(initialFilters.eessId ? { id: initialFilters.eessId, nombre: initialFilters.eessNombre || "" } : null);
    setEessInput(initialFilters.eessNombre || "");

    setRolSel(initialFilters.rolId ? { id: initialFilters.rolId, nombre: initialFilters.rolNombre || "" } : null);
    setRolInput(initialFilters.rolNombre || "");
  }, [initialFilters]);

  // === cancelar requests previos ===
  const cancelCli = () => { try { cliAbort.current?.abort(); } catch {} };
  const cancelEess = () => { try { eessAbort.current?.abort(); } catch {} };
  const cancelRol = () => { try { rolAbort.current?.abort(); } catch {} };
  const cancelCod = () => { try { codAbort.current?.abort(); } catch {} };
  const cancelNom = () => { try { nomAbort.current?.abort(); } catch {} };

  // === fetch paginado (cliente / eess / usuario) ===
  const fetchAutocomplete = async (tipo, q, page, signal) => {
    const qTrim = (q ?? "").trim();
    let url;

    if (tipo === "cliente") {
      url = apiRoutes.clientes.autocompleteUrl({
        q: qTrim,
        page: page ?? 0,
        size: PAGE_SIZE,
        tipo: "cliente",
        browse: true, // permite explorar sin q
      });
    } else if (tipo === "eess") {
      url = apiRoutes.eessHabilitadas.autocompleteUrl("eess", qTrim, page ?? 0, PAGE_SIZE);
    } else if (tipo === "usuario") {
      const params = new URLSearchParams();
      if (qTrim) params.set("q", qTrim); // backend busca por código o nombre
      params.set("page", String(page ?? 0));
      params.set("size", String(PAGE_SIZE));
      url = `${apiRoutes.usuarios.autocomplete}?${params.toString()}`;
    }
    else if (tipo === "rol") {
      url = apiRoutes.roles.autocompleteUrl(qTrim, page ?? 0, PAGE_SIZE);
      const data = await apiFetchJson(url, { signal });
      const items = data?.items ?? [];
      const normalized = items.map((x) => ({
        id: x.id,
        nombre: x.label,
      }));
      return {
        list: normalized,
        hasMore: Boolean(data?.hasMore),
        page: data?.page ?? page ?? 0,
      };
    }
    else {
      throw new Error("Tipo inválido para autocomplete");
    }

    const data = await apiFetchJson(url, { signal });
    const items = data?.items ?? [];

    if (tipo === "usuario") {
      const normalized = items.map((x) => {
        const id = x.id;
        const codigo = (x.codigo ?? "").toString().trim();
        const nombre = (x.nombre ?? "").toString().trim();
        const label = codigo && nombre ? `${codigo} — ${nombre}` : (codigo || nombre || String(id));
        return { id, codigo, nombre, label };
      });
      return {
        list: normalized,
        hasMore: Boolean(data?.hasMore),
        page: data?.page ?? page ?? 0,
      };
    } else {
      // cliente / eess normalizados a {id, nombre}
      const normalized = items.map((x) => {
        const id = x.id;
        const codigo = (x.codigo ?? "").toString().trim();
        const nombre = (x.nombre ?? x.label ?? "").toString().trim();
        const label = x.label ?? (codigo && nombre ? `${codigo} - ${nombre}` : (nombre || String(id)));
        return { id, nombre: label };
      });
      return {
        list: normalized,
        hasMore: Boolean(data?.hasMore),
        page: data?.page ?? page ?? 0,
      };
    }
  };

  // ===== Buscar Clientes =====
  useEffect(() => {
    if (!cliOpen) return;
    cancelCli();
    const ctrl = new AbortController();
    cliAbort.current = ctrl;
    const q = debCli;
    setCliLoading(true);
    (async () => {
      try {
        const { list, hasMore, page } = await fetchAutocomplete("cliente", q, 0, ctrl.signal);
        setCliItems(list);
        setCliHasMore(hasMore);
        setCliPage(page);
      } catch (e) {
        if (!isAbort(e)) console.error("Autocomplete cliente:", e);
      } finally {
        setCliLoading(false);
      }
    })();
    return cancelCli;
  }, [cliOpen, debCli]);

  const onClientScroll = (e) => {
    const el = e.currentTarget;
    if (!cliHasMore || cliLoading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      const q = debCli;
      const next = cliPage + 1;
      const ctrl = new AbortController();
      cliAbort.current = ctrl;
      setCliLoading(true);
      fetchAutocomplete("cliente", q, next, ctrl.signal)
        .then(({ list, hasMore, page }) => {
          setCliItems((prev) => [...prev, ...list]);
          setCliHasMore(hasMore);
          setCliPage(page);
        })
        .finally(() => setCliLoading(false));
    }
  };

  // ===== Buscar EE.SS =====
  useEffect(() => {
    if (!eessOpen) return;
    cancelEess();
    const ctrl = new AbortController();
    eessAbort.current = ctrl;
    const q = debEess;
    setEessLoading(true);
    (async () => {
      try {
        const { list, hasMore, page } = await fetchAutocomplete("eess", q, 0, ctrl.signal);
        setEessItems(list);
        setEessHasMore(hasMore);
        setEessPage(page);
      } catch (e) {
        if (!isAbort(e)) console.error("Autocomplete EESS:", e);
      } finally {
        setEessLoading(false);
      }
    })();
    return cancelEess;
  }, [eessOpen, debEess]);

  //Rol
  useEffect(() => {
    if (!rolOpen) return;
    cancelRol();
    const ctrl = new AbortController();
    rolAbort.current = ctrl;
    const q = debRol;
    setRolLoading(true);
    (async () => {
      try {
        const { list, hasMore, page } = await fetchAutocomplete("rol", q, 0, ctrl.signal);
        setRolItems(list);
        setRolHasMore(hasMore);
        setRolPage(page);
      } catch (e) {
        if (!isAbort(e)) console.error("Autocomplete rol:", e);
      } finally {
        setRolLoading(false);
      }
    })();
    return cancelRol;
  }, [rolOpen, debRol]);

  const onEessScroll = (e) => {
    const el = e.currentTarget;
    if (!eessHasMore || eessLoading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      const q = debEess;
      const next = eessPage + 1;
      const ctrl = new AbortController();
      eessAbort.current = ctrl;
      setEessLoading(true);
      fetchAutocomplete("eess", q, next, ctrl.signal)
        .then(({ list, hasMore, page }) => {
          setEessItems((prev) => [...prev, ...list]);
          setEessHasMore(hasMore);
          setEessPage(page);
        })
        .finally(() => setEessLoading(false));
    }
  };

  //Rol
  const onRolScroll = (e) => {
    const el = e.currentTarget;
    if (!rolHasMore || rolLoading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      const q = debRol;
      const next = rolPage + 1;
      const ctrl = new AbortController();
      rolAbort.current = ctrl;
      setRolLoading(true);
      fetchAutocomplete("rol", q, next, ctrl.signal)
          .then(({ list, hasMore, page }) => {
            setRolItems((prev) => [...prev, ...list]);
            setRolHasMore(hasMore);
            setRolPage(page);
          })
          .finally(() => setRolLoading(false));
    }
  };

  // ===== Buscar Usuarios (Código) =====
  useEffect(() => {
    if (!codOpen) return;
    cancelCod();
    const ctrl = new AbortController();
    codAbort.current = ctrl;
    const q = debCod;
    setCodLoading(true);
    (async () => {
      try {
        const { list, hasMore, page } = await fetchAutocomplete("usuario", q, 0, ctrl.signal);
        setCodItems(list);
        setCodHasMore(hasMore);
        setCodPage(page);
      } catch (e) {
        if (!isAbort(e)) console.error("Autocomplete usuario (código):", e);
      } finally {
        setCodLoading(false);
      }
    })();
    return cancelCod;
  }, [codOpen, debCod]);

  const onCodScroll = (e) => {
    const el = e.currentTarget;
    if (!codHasMore || codLoading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      const q = debCod;
      const next = codPage + 1;
      const ctrl = new AbortController();
      codAbort.current = ctrl;
      setCodLoading(true);
      fetchAutocomplete("usuario", q, next, ctrl.signal)
        .then(({ list, hasMore, page }) => {
          setCodItems((prev) => [...prev, ...list]);
          setCodHasMore(hasMore);
          setCodPage(page);
        })
        .finally(() => setCodLoading(false));
    }
  };

  // ===== Buscar Usuarios (Nombre) =====
  useEffect(() => {
    if (!nomOpen) return;
    cancelNom();
    const ctrl = new AbortController();
    nomAbort.current = ctrl;
    const q = debNom;
    setNomLoading(true);
    (async () => {
      try {
        const { list, hasMore, page } = await fetchAutocomplete("usuario", q, 0, ctrl.signal);
        setNomItems(list);
        setNomHasMore(hasMore);
        setNomPage(page);
      } catch (e) {
        if (!isAbort(e)) console.error("Autocomplete usuario (nombre):", e);
      } finally {
        setNomLoading(false);
      }
    })();
    return cancelNom;
  }, [nomOpen, debNom]);

  const onNomScroll = (e) => {
    const el = e.currentTarget;
    if (!nomHasMore || nomLoading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      const q = debNom;
      const next = nomPage + 1;
      const ctrl = new AbortController();
      nomAbort.current = ctrl;
      setNomLoading(true);
      fetchAutocomplete("usuario", q, next, ctrl.signal)
        .then(({ list, hasMore, page }) => {
          setNomItems((prev) => [...prev, ...list]);
          setNomHasMore(hasMore);
          setNomPage(page);
        })
        .finally(() => setNomLoading(false));
    }
  };

  // === Cerrar dropdowns click afuera ===
  useEffect(() => {
    const onDoc = (e) => {
      if (cliBoxRef.current && !cliBoxRef.current.contains(e.target)) setCliOpen(false);
      if (eessBoxRef.current && !eessBoxRef.current.contains(e.target)) setEessOpen(false);
      //Rol
      if (rolBoxRef.current && !rolBoxRef.current.contains(e.target)) setRolOpen(false);
      if (codBoxRef.current && !codBoxRef.current.contains(e.target)) setCodOpen(false);
      if (nomBoxRef.current && !nomBoxRef.current.contains(e.target)) setNomOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // === Submit ===
  const submit = (e) => {
    e.preventDefault();

    // Si no hubo selección desde el autocomplete, usamos lo que el usuario tipeó
    const codigoToSend = (codigo || codInput || "").trim();
    const nombreToSend = (nombre || nomInput || "").trim();

    onSubmit({
      codigo: codigoToSend,
      nombre: nombreToSend,
      estado, // "" => Todos/ignorar
      clienteId: clienteSel?.id ?? "",
      clienteNombre: clienteSel?.nombre ?? cliInput.trim() ?? "",
      eessId: eessSel?.id ?? "",
      eessNombre: eessSel?.nombre ?? eessInput.trim() ?? "",
      //Rol
      rolId: rolSel?.id ?? "",
      rolNombre: rolSel?.nombre ?? rolInput.trim() ?? "",
    });
    onClose();
  };

  // ======= Render =======
  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer-panel overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-header">
          <h2 className="text-title">Filtros</h2>
          <button onClick={onClose} className="btn-cancel" title="Cerrar">
            &times;
          </button>
        </div>

        <form onSubmit={submit} className="drawer-body space-y-3">
          {/* Código (Autocomplete Usuario) */}
          <div ref={codBoxRef} className="relative">
            <label className="form-label-fil">Código:</label>
            <input
              type="text"
              className="input"
              placeholder="Buscar por código o nombre…"
              value={codInput}
              onChange={(e) => {
                const v = e.target.value;
                setCodInput(v);
                setCodOpen(true);
              }}
              onFocus={() => setCodOpen(true)}
            />
            {codOpen && (
              <div
                className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl max-h-72 overflow-auto"
                onScroll={onCodScroll}
              >
                {codItems.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-gray-50"
                    onClick={() => {
                      // Al elegir, poblar código + nombre y cerrar ambos autocompletes
                      setCodigo(u.codigo || "");
                      setNombre(u.nombre || "");
                      setCodInput(u.label || u.codigo || u.nombre || "");
                      setNomInput(u.nombre || "");
                      setCodOpen(false);
                      setNomOpen(false);
                    }}
                  >
                    {u.label}
                  </button>
                ))}
                {codLoading && <div className="px-3 py-2 text-sm text-gray-500">Cargando…</div>}
                {!codLoading && codItems.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">Sin resultados</div>
                )}
              </div>
            )}
          </div>

          {/* Nombre (Autocomplete Usuario) */}
          <div ref={nomBoxRef} className="relative">
            <label className="form-label-fil">Nombre:</label>
            <input
              type="text"
              className="input"
              placeholder="Buscar por nombre o código…"
              value={nomInput}
              onChange={(e) => {
                const v = e.target.value;
                setNomInput(v);
                setNomOpen(true);
              }}
              onFocus={() => setNomOpen(true)}
            />
            {nomOpen && (
              <div
                className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl max-h-72 overflow-auto"
                onScroll={onNomScroll}
              >
                {nomItems.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-gray-50"
                    onClick={() => {
                      // Al elegir, poblar nombre + código y cerrar ambos
                      setNombre(u.nombre || "");
                      setCodigo(u.codigo || "");
                      setNomInput(u.nombre || "");
                      setCodInput(u.label || u.codigo || u.nombre || "");
                      setNomOpen(false);
                      setCodOpen(false);
                    }}
                  >
                    {u.label}
                  </button>
                ))}
                {nomLoading && <div className="px-3 py-2 text-sm text-gray-500">Cargando…</div>}
                {!nomLoading && nomItems.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">Sin resultados</div>
                )}
              </div>
            )}
          </div>

          {/* Rol autocomplete */}
          <div ref={rolBoxRef} className="relative">
            <label className="form-label-fil">Rol:</label>
            <input
                type="text"
                className="input"
                placeholder="Buscar rol..."
                value={rolInput}
                onChange={(e) => {
                  setRolInput(e.target.value);
                  setRolOpen(true);
                  setRolSel(null);
                }}
                onFocus={() => setRolOpen(true)}
            />
            {rolOpen && (
                <div
                    className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl max-h-72 overflow-auto"
                    onScroll={onRolScroll}
                >
                  {rolItems.map((r) => (
                      <button
                          key={r.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-50"
                          onClick={() => {
                            setRolSel(r);
                            setRolInput(r.nombre);
                            setRolOpen(false);
                          }}
                      >
                        {r.nombre}
                      </button>
                  ))}
                  {rolLoading && (
                      <div className="px-3 py-2 text-sm text-gray-500">Cargando…</div>
                  )}
                  {!rolLoading && rolItems.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">Sin resultados</div>
                  )}
                </div>
            )}
          </div>

          {/* Estado */}
          <div className="relative">
            <label className="form-label-fil">Estado:</label>
            <select
              className="input"
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="ACTIVO">ACTIVO</option>
              <option value="INACTIVO">INACTIVO</option>
            </select>
          </div>

          {/* Cliente autocomplete */}
          <div ref={cliBoxRef} className="relative">
            <label className="form-label-fil">Cliente:</label>
            <input
              type="text"
              className="input"
              placeholder="Buscar cliente..."
              value={cliInput}
              onChange={(e) => {
                setCliInput(e.target.value);
                setCliOpen(true);
                setClienteSel(null);
              }}
              onFocus={() => setCliOpen(true)}
            />
            {cliOpen && (
              <div
                className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl max-h-72 overflow-auto"
                onScroll={onClientScroll}
              >
                {cliItems.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-gray-50"
                    onClick={() => {
                      setClienteSel(c);
                      setCliInput(c.nombre);
                      setCliOpen(false);
                    }}
                  >
                    {c.nombre}
                  </button>
                ))}
                {cliLoading && (
                  <div className="px-3 py-2 text-sm text-gray-500">Cargando…</div>
                )}
                {!cliLoading && cliItems.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">Sin resultados</div>
                )}
              </div>
            )}
          </div>

          {/* EE.SS autocomplete */}
          <div ref={eessBoxRef} className="relative">
            <label className="form-label-fil">EE.SS:</label>
            <input
              type="text"
              className="input"
              placeholder="Buscar EE.SS..."
              value={eessInput}
              onChange={(e) => {
                setEessInput(e.target.value);
                setEessOpen(true);
                setEessSel(null);
              }}
              onFocus={() => setEessOpen(true)}
            />
            {eessOpen && (
              <div
                className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl max-h-72 overflow-auto"
                onScroll={onEessScroll}
              >
                {eessItems.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-gray-50"
                    onClick={() => {
                      setEessSel(c);
                      setEessInput(c.nombre);
                      setEessOpen(false);
                    }}
                  >
                    {c.nombre}
                  </button>
                ))}
                {eessLoading && (
                  <div className="px-3 py-2 text-sm text-gray-500">Cargando…</div>
                )}
                {!eessLoading && eessItems.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">Sin resultados</div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="text-right mt-4">
            <button type="submit" className="btn">
              Aplicar Filtros
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
